// Reusable FAQ accordion with FAQPage JSON-LD output. The JSON-LD is rendered
// inline so it ends up in the SSR HTML for any page that uses this block.
import { useState } from "react";
import { faqPageJsonLd, type FaqItem } from "@/lib/seo";

interface Props {
  items: FaqItem[];
  title?: string;
  eyebrow?: string;
  className?: string;
}

export function FaqBlock({ items, title, eyebrow, className }: Props) {
  const [open, setOpen] = useState<number | null>(0);
  if (!items.length) return null;
  const ld = faqPageJsonLd(items);

  return (
    <section className={`mx-auto max-w-3xl px-6 py-16 ${className ?? ""}`}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
      />
      {(eyebrow || title) && (
        <div className="mb-8 text-center">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          {title && (
            <h2 className="mt-3 font-display text-3xl text-foreground sm:text-4xl">
              {title}
            </h2>
          )}
        </div>
      )}
      <ul className="divide-y divide-border/60 border-y border-border/60">
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="flex w-full items-start justify-between gap-4 py-4 text-left"
                aria-expanded={isOpen}
              >
                <span className="font-display text-lg text-foreground">{it.q}</span>
                <span className="mt-1 text-muted-foreground" aria-hidden>
                  {isOpen ? "−" : "+"}
                </span>
              </button>
              {isOpen && (
                <p className="pb-5 text-sm leading-relaxed text-muted-foreground">
                  {it.a}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
